/**
 * CR429: "Why Hidden" diagnostics panel for admin preview.
 *
 * Compact collapsible panel showing per-section visibility status
 * with reason codes and missing fields.
 */

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronDown, Eye, EyeOff, AlertTriangle, Info } from "lucide-react";
import type { DealCardDiagnosticsResult, SectionDiagnostic } from "@/lib/deal-cards/diagnostics/dealCardVisibilityDiagnostics";

interface WhyHiddenPanelProps {
  diagnostics: DealCardDiagnosticsResult | null;
  className?: string;
}

const REASON_CODE_LABELS: Record<string, string> = {
  TOGGLE_OFF: "Toggle off",
  NO_BLOCK_MAPPING: "No block mapping",
  NO_DEAL_PAYLOAD: "No deal payload",
  MISSING_REQUIRED_FIELDS: "Missing fields",
};

const REASON_CODE_COLORS: Record<string, string> = {
  TOGGLE_OFF: "text-muted-foreground",
  NO_BLOCK_MAPPING: "text-warning",
  NO_DEAL_PAYLOAD: "text-destructive",
  MISSING_REQUIRED_FIELDS: "text-destructive",
};

function SectionRow({ diag }: { diag: SectionDiagnostic }) {
  const Icon = diag.visible ? Eye : EyeOff;
  const iconColor = diag.visible ? "text-primary" : "text-destructive";

  return (
    <div className="flex items-start gap-2 py-1 px-2 rounded hover:bg-muted/40 transition-colors">
      <Icon className={`h-3 w-3 mt-0.5 flex-shrink-0 ${iconColor}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-mono font-medium">{diag.sectionId}</span>
          {diag.reason_codes.map((code) => (
            <Badge
              key={code}
              variant="outline"
              className={`text-[9px] px-1 py-0 ${REASON_CODE_COLORS[code] ?? "text-muted-foreground"}`}
            >
              {REASON_CODE_LABELS[code] ?? code}
            </Badge>
          ))}
        </div>
        {diag.missing_fields.length > 0 && (
          <div className="text-[9px] text-destructive/80 font-mono mt-0.5">
            missing: {diag.missing_fields.join(", ")}
          </div>
        )}
        {diag.notes.length > 0 && (
          <div className="text-[9px] text-muted-foreground mt-0.5">
            {diag.notes.map((n, i) => (
              <div key={i}>💡 {n}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function WhyHiddenPanel({ diagnostics, className = "" }: WhyHiddenPanelProps) {
  const [open, setOpen] = useState(false);

  if (!diagnostics) return null;

  const { summary, diagnostics: sectionDiags, section_order } = diagnostics;
  const hasHidden = summary.hidden_count > 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={className}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-between h-7 px-2 text-[10px] font-mono hover:bg-muted/50"
        >
          <div className="flex items-center gap-1.5">
            {hasHidden ? (
              <AlertTriangle className="h-3 w-3 text-warning" />
            ) : (
              <Info className="h-3 w-3 text-muted-foreground" />
            )}
            <span>
              Why Hidden: {summary.visible_count} visible / {summary.hidden_count} hidden
            </span>
          </div>
          <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1">
        <div className="border rounded bg-background/50 divide-y divide-border/50 max-h-[300px] overflow-auto">
          {/* Hidden sections first */}
          {summary.hidden_sections.length > 0 && (
            <div className="p-1.5">
              <div className="text-[9px] font-medium text-destructive px-2 py-0.5 mb-0.5">
                HIDDEN ({summary.hidden_count})
              </div>
              {summary.hidden_sections.map((d) => (
                <SectionRow key={d.sectionId} diag={d} />
              ))}
            </div>
          )}
          {/* Visible sections */}
          <div className="p-1.5">
            <div className="text-[9px] font-medium text-muted-foreground px-2 py-0.5 mb-0.5">
              VISIBLE ({summary.visible_count})
            </div>
            {section_order
              .filter((id) => sectionDiags[id]?.visible)
              .map((id) => (
                <SectionRow key={id} diag={sectionDiags[id]} />
              ))}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Compact summary for DealSourcePicker tooltip.
 */
export function DiagnosticsSummaryBadge({
  diagnostics,
  className = "",
}: {
  diagnostics: DealCardDiagnosticsResult | null;
  className?: string;
}) {
  if (!diagnostics || diagnostics.summary.hidden_count === 0) return null;

  const hidden = diagnostics.summary.hidden_sections;
  const topHidden = hidden.slice(0, 3);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={`text-[9px] font-mono cursor-help text-warning border-warning/30 ${className}`}
          >
            {diagnostics.summary.hidden_count} hidden
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs font-mono max-w-sm">
          <div className="font-medium mb-1">Hidden sections:</div>
          {topHidden.map((d) => (
            <div key={d.sectionId}>
              • {d.sectionId}: {d.reason_codes.map((c) => REASON_CODE_LABELS[c] ?? c).join(", ")}
            </div>
          ))}
          {hidden.length > 3 && (
            <div className="text-muted-foreground mt-0.5">
              +{hidden.length - 3} more…
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
